import type { CSSProperties } from "react";
import { siteBuilderPartOptionKey } from "../siteBuilderPartOptions";
import { siteBuilderMediaPresentation } from "../siteBuilderBlockPresentation";
import { siteBuilderRebasedAuthoritativeStyleVariables } from "../siteBuilderStyles";
import type { SiteBuilderStyleRecord } from "../siteBuilderStyles";
import type { ShopTemplateBlock } from "../types";

const MORROW_MEDIA_SOURCE_FIELDS = [
    "embedUrl",
    "image",
    "imageUrl",
    "mediaUuid",
    "src",
    "url",
    "videoUrl",
] as const;

export function morrowEditorMediaPlaceholderSource(
    block: ShopTemplateBlock,
    placeholderSource: string,
): string {
    const content = block.content ?? {};
    const hasExplicitSource = MORROW_MEDIA_SOURCE_FIELDS.some((field) => (
        Object.prototype.hasOwnProperty.call(content, field)
    ));

    return hasExplicitSource ? "" : placeholderSource;
}

/**
 * Older editor selections could treat the visible image of an atomic media
 * block as a nested `image` part. Choosing media in that scope stored the
 * same URL both as the real image source and as `imageBackgroundImage`.
 * Preserve legitimate, different background artwork while removing this
 * exact duplicate layer from rendering.
 */
export function morrowAtomicMediaPartStyle(
    block: ShopTemplateBlock,
    part: string,
    style: CSSProperties,
): CSSProperties {
    if ((block.type !== "image" && block.type !== "media") || part !== "image") {
        return style;
    }

    const presentation = siteBuilderMediaPresentation(block.content);
    const backgroundImage = block.options?.[
        siteBuilderPartOptionKey(part, "backgroundImage")
    ];

    if (
        presentation.kind !== "image"
        || typeof backgroundImage !== "string"
        || backgroundImage.trim() !== presentation.source.trim()
    ) {
        return style;
    }

    const resolvedStyle: SiteBuilderStyleRecord = {
        ...style,
        backgroundImage: undefined,
    };

    return {
        ...resolvedStyle,
        ...siteBuilderRebasedAuthoritativeStyleVariables(
            style as SiteBuilderStyleRecord,
            resolvedStyle,
        ),
    };
}
